import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Grid, Box } from '@mui/material';
import GlobalTabsComponent from '../../GlobalComponent/GlobalTabsComponent/GlobalTabsComponent';
import PendingRequestsList from './PendingRequestsList';
import RejectedRequestsList from './RejectedRequestsList';
import pendingRequestsStyle from '../../Styles/ComponentStyles/PendingRequests/PendingRequests.module.scss';
import agent from '../../service/api';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import { PendingAgentRow } from './PendingRequestsList';
import { RejectedAgentRow } from './RejectedRequestsList';
import { parseLicensesHold } from './LicensesHoldCell';
import moment from 'moment';

type RequestTabValue = 'verified' | 'pendingVerification' | 'rejected';

function getAgentStatusKey(row: Record<string, any>): string {
    const raw = row.status ?? row.status_name ?? row.user_status_name ?? '';
    return String(raw).toLowerCase().replace(/\s+/g, ' ').trim();
}

function matchesStatusFilter(row: Record<string, any>, status: 'pending' | 'registered'): boolean {
    const key = getAgentStatusKey(row);
    if (status === 'pending') {
        return key === 'pending' || key === 'pending for approval';
    }
    return key === 'registered';
}

function normalizeAgentList(response: any): any[] {
    if (!response || response.isAxiosError) return [];
    if (Array.isArray(response)) return response;
    if (response.status === false) return [];
    const d = response.data;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray(d.list_of_agents)) return d.list_of_agents;
    if (d && Array.isArray(d.agents)) return d.agents;
    if (d && Array.isArray(d.list)) return d.list;
    return [];
}

function deriveEmailVerification(row: Record<string, any>): 'Verified' | 'Pending' {
    const statusName = String(row.status_name ?? '').toLowerCase();
    if (statusName === 'pending for approval') return 'Verified';
    if (statusName === 'registered') return 'Pending';

    // fallback to old field-based logic
    const v = row.email_verified ?? row.is_email_verified ?? row.user_email_verified;
    if (v === true || v === 1 || v === '1' || v === 'true') return 'Verified';
    const s = String(row.email_verification_status ?? row.verification_status ?? '').toLowerCase();
    if (s === 'verified' || s === 'active') return 'Verified';
    return 'Pending';
}

function mapAgentToPendingRow(item: Record<string, any>): PendingAgentRow {
    const first = item.firstname ?? '';
    const last = item.lastname ?? '';
    const name = `${first} ${last}`.trim() || '—';
    const created = item.created_date ?? null;
    return {
        ...item,
        user_id: Number(item.user_id),
        name,
        email: item.email ?? '',
        requestedDate: created ? moment(created).format('MM/DD/YYYY  hh:mm A') : '—',
        emailVerification: deriveEmailVerification(item),
        licensesHold: parseLicensesHold(item.licenses_hold),
    };
}

function mapAgentToRejectedRow(item: Record<string, any>): RejectedAgentRow {
    const first = item.firstname ?? '';
    const last = item.lastname ?? '';
    const name = `${first} ${last}`.trim() || '—';
    const requested = item.created_date ?? null;
    const rejectedAt = item.rejection_note_at ?? null;
    return {
        ...item,
        user_id: Number(item.user_id),
        name,
        email: item.email ?? '',
        requestedDate: requested ? moment(requested).format('MM/DD/YYYY  hh:mm A') : '—',
        rejectedDate: rejectedAt ? moment(rejectedAt).format('MM/DD/YYYY  hh:mm A') : '—',
        rejectedBy: item.rejected_by ?? '—',
        rejectedReason: item.rejection_note ?? '—',
        licensesHold: parseLicensesHold(item.licenses_hold),
    };
}

const PendingRequestsWrapper = () => {
    const { setFlagLoader } = useContext(UserContext) as UserContextPayload;
    const { showSnackbar } = useSnackbar();

    const [activeTab, setActiveTab] = useState<RequestTabValue>('verified');
    const [verifiedRows, setVerifiedRows] = useState<PendingAgentRow[]>([]);
    const [pendingVerificationRows, setPendingVerificationRows] = useState<PendingAgentRow[]>([]);
    const [rejectedRows, setRejectedRows] = useState<RejectedAgentRow[]>([]);
    const [verifiedCount, setVerifiedCount] = useState<number>(0);
    const [pendingVerificationCount, setPendingVerificationCount] = useState<number>(0);
    const [rejectedCount, setRejectedCount] = useState<number>(0);

    const fetchAllData = useCallback(async () => {
        setFlagLoader(true);
        try {
            const [pendingRes, registeredRes, rejectedRes] = await Promise.all([
                agent.getAgentListByFilterAPI.get({ status: 'pending' }),
                agent.getAgentListByFilterAPI.get({ status: 'registered' }),
                agent.getAgentListByFilterAPI.get({ status: 'rejected' }),
            ]);

            if (!pendingRes?.isAxiosError && pendingRes?.status !== false) {
                const list = normalizeAgentList(pendingRes)
                    .filter((item) => matchesStatusFilter(item, 'pending'))
                    .map(mapAgentToPendingRow)
                    .filter((r) => !Number.isNaN(r.user_id));
                setVerifiedRows(list);
                setVerifiedCount(list.length);
            } else {
                showSnackbar('Failed to load verified requests.', 'error');
            }

            if (!registeredRes?.isAxiosError && registeredRes?.status !== false) {
                const list = normalizeAgentList(registeredRes)
                    .filter((item) => matchesStatusFilter(item, 'registered'))
                    .map(mapAgentToPendingRow)
                    .filter((r) => !Number.isNaN(r.user_id));
                setPendingVerificationRows(list);
                setPendingVerificationCount(list.length);
            } else {
                showSnackbar('Failed to load pending verification requests.', 'error');
            }

            if (!rejectedRes?.isAxiosError && rejectedRes?.status !== false) {
                setRejectedCount(rejectedRes?.data?.count ?? 0);
                const list = normalizeAgentList(rejectedRes);
                setRejectedRows(list.map(mapAgentToRejectedRow).filter((r) => !Number.isNaN(r.user_id)));
            } else {
                showSnackbar('Failed to load rejected requests.', 'error');
            }
        } catch {
            showSnackbar('Failed to load requests.', 'error');
        } finally {
            setFlagLoader(false);
        }
    }, [setFlagLoader, showSnackbar]);

    useEffect(() => {
        fetchAllData();
    }, []);

    const tabs = [
        { label: `Verified (${verifiedCount})`, value: 'verified' },
        { label: `Pending Verification (${pendingVerificationCount})`, value: 'pendingVerification' },
        { label: `Rejected (${rejectedCount})`, value: 'rejected' },
    ];

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
        setActiveTab(newValue as RequestTabValue);
    };

    return (
        <Grid container direction="column" className={pendingRequestsStyle.pendingRequestsWrapper}>
            <Grid item xs={12}>
                <Box className={pendingRequestsStyle.tabsContainer}>
                    <GlobalTabsComponent
                        value={activeTab}
                        onChange={handleTabChange}
                        tabs={tabs}
                    />
                </Box>
            </Grid>

            <Grid item xs={12} className={pendingRequestsStyle.tabContent}>
                {activeTab === 'verified' && (
                    <PendingRequestsList rows={verifiedRows} onRefresh={fetchAllData} />
                )}
                {activeTab === 'pendingVerification' && (
                    <PendingRequestsList rows={pendingVerificationRows} onRefresh={fetchAllData} />
                )}
                {activeTab === 'rejected' && (
                    <RejectedRequestsList rows={rejectedRows} onRefresh={fetchAllData} />
                )}
            </Grid>
        </Grid>
    );
};

export default PendingRequestsWrapper;